import { setupSessionExpirationCheck } from '@shared/utils';
import { useEffect } from 'react';
import { useIsSignin } from './useAuth';

let globalSessionManagerInitialized = false;

/**
 * Hook to manage session expiration checks globally.
 *
 * Re-runs when the auth state flips so that a fresh login (no user in
 * localStorage when the app first mounted) still triggers
 * `setupSessionExpirationCheck`. The internal `setupSessionExpirationCheck`
 * early-returns if no user is in storage, so calling it before sign-in is a
 * no-op — the gate here on `isSignin` is the explicit signal to retry once
 * Redux confirms the user is authenticated.
 *
 * Once the underlying axios interceptors and activity listeners are
 * registered they persist for the lifetime of the page, so the global flag
 * prevents duplicate registrations on subsequent re-renders or relogins.
 */
export const useSessionManager = (): void => {
  const isSignin = useIsSignin();

  useEffect(() => {
    if (!isSignin || globalSessionManagerInitialized) return;
    setupSessionExpirationCheck();
    globalSessionManagerInitialized = true;
  }, [isSignin]);
};

/** Test-only: reset the module-scoped initialization flag. */
export const _resetSessionManagerForTests = (): void => {
  globalSessionManagerInitialized = false;
};
